import store from '../store'
import { ErrorType, pushException } from '../reducers/errorReducer'
import { stripHtml } from 'string-strip-html'
import { isAppContext } from './appHelper'
import { AppContext } from '../config/appConfig'
import EmailAddressDetails = Office.EmailAddressDetails

export type MessageAttachment = {
    type: string
    name: string
    url: string
    isInline: boolean
    itemId?: string
}

type MessageForm = {
    //An array of strings containing the email addresses or an array containing an | EmailAddressDetails object for each of the recipients on the To line. The array is limited to a maximum of 100 entries.
    toRecipients: string[]
    //An array of strings containing the email addresses or an array containing an | EmailAddressDetails object for each of the recipients on the Cc line. The array is limited to a maximum of 100 entries.
    ccRecipients?: string[]
    //An array of strings containing the email addresses or an array containing an | EmailAddressDetails object for each of the recipients on the Bcc line. The array is limited to a maximum of 100 entries.
    bccRecipients?: string[]
    //A string containing the subject of the message. The string is limited to a maximum of 255 characters.
    subject?: string
    //The HTML body of the message. The body content is limited to a maximum size of 32 KB.
    htmlBody?: string
    //An array of JSON objects that are either file or item attachments.
    attachments?: MessageAttachment[]
}

type AppointmentForm = Partial<Omit<Office.AppointmentForm, 'requiredAttendees'>> & {
    requiredAttendees: string[]
    optionalAttendees?: string[]
}

export function outlookDisplayNewMessageForm(
    parameters: MessageForm,
    callback?: (asyncResult?: Office.AsyncResult<void>) => void,
): void {
    try {
        if ((!isOfficeInitialized() || isAppContext([AppContext.Compose])) && !(parameters.attachments?.length > 0)) {
            parameters.htmlBody =
                parameters.htmlBody &&
                stripHtml(parameters.htmlBody, {
                    dumpLinkHrefsNearby: { enabled: true, putOnNewLine: false, wrapHeads: '(', wrapTails: ')' },
                }).result

            window.open(
                [
                    `mailto:${parameters.toRecipients.join(';')}`,
                    parameters.bccRecipients ? `&bcc=${parameters.bccRecipients.join(';')}` : undefined,
                    `?subject=${parameters.subject || ''}`,
                    parameters.htmlBody ? `&body=${encodeURIComponent(parameters.htmlBody)}` : undefined,
                ]
                    .filter(value => value !== undefined)
                    .join(''),
                '_blank',
            )
            callback?.(undefined)
        } else {
            assertOfficeInitialized()

            parameters.htmlBody = parameters.htmlBody?.replaceAll('\n', '<br/>')

            if (Office.context.requirements.isSetSupported('Mailbox', '1.9')) {
                Office.context.mailbox.displayNewMessageFormAsync(parameters, result => callback?.(result))
            } else {
                Office.context.mailbox.displayNewMessageForm(parameters)
                callback?.(undefined)
            }
        }
    } catch (e) {
        store.dispatch(pushException({ id: e.message, type: ErrorType.Outlook, message: e.message }, e))
    }
}

export function outlookDisplayNewAppointmentForm(
    parameters: AppointmentForm,
    callback?: (asyncResult?: Office.AsyncResult<void>) => void,
): void {
    try {
        const _parameters: Office.AppointmentForm = {
            end: new Date(),
            start: new Date(),
            subject: '',
            body: '',
            location: '',
            resources: [],
            ...parameters,
            optionalAttendees: (parameters.optionalAttendees as unknown as EmailAddressDetails[]) || [],
            requiredAttendees: (parameters.requiredAttendees as unknown as EmailAddressDetails[]) || [],
        }

        assertOfficeInitialized()

        if (Office.context.requirements.isSetSupported('Mailbox', '1.9')) {
            Office.context.mailbox.displayNewAppointmentFormAsync(_parameters, result => callback?.(result))
        } else {
            Office.context.mailbox.displayNewAppointmentForm(_parameters)
            callback?.(undefined)
        }
    } catch (e) {
        store.dispatch(pushException({ id: e.message, type: ErrorType.Outlook, message: e.message }, e))
    }
}

export function outlookDisplayMessageForm(
    messageId: string,
    callback?: (asyncResult?: Office.AsyncResult<void>) => void,
): void {
    try {
        if (isOfficeInitialized() && messageId) {
            if (Office.context.requirements.isSetSupported('Mailbox', '1.9')) {
                Office.context.mailbox.displayMessageFormAsync(messageId, result => callback?.(result))
            } else {
                Office.context.mailbox.displayMessageForm(messageId)
                callback?.(undefined)
            }
        }
    } catch (e) {
        store.dispatch(pushException({ id: e.message, type: ErrorType.Outlook, message: e.message }, e))
    }
}

export function isOfficeInitialized(): boolean {
    return Office.hasOwnProperty('context') && Office.context.hasOwnProperty('ui')
}

export function assertOfficeInitialized(): void {
    if (!isOfficeInitialized()) {
        throw new Error('Deze handeling kan alleen vanuit Outlook worden uitgevoerd.')
    }
}
